import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * - Open and close are both interruptible.
 * - Mobile scroll lock uses a touch guard instead of html { overflow: hidden } (which resized
 *   the viewport and moved fixed buttons). The guard blocks page scrolling but lets any scrollable
 *   area inside the menu scroll (the menu list itself included).
 * - Arabic (RTL): the drawer never leaves the screen. Sliding it past the right edge made the
 *   page wider than the screen for a moment and Chrome zoomed the whole page out and back
 *   (measured: innerWidth 384 -> 403), which is what moved the WhatsApp button. In RTL the drawer
 *   box stays in place and is revealed with clip-path while its content slides; it looks the
 *   same as a slide but nothing ever sticks out of the page.
 * - Motion: the drawer keyframes hold the moving property only, in pixels. A transform-only
 *   animation runs on the compositor; with `visibility` in the same keyframes it ran on the main
 *   thread and stuttered whenever the page was busy. A fresh open first waits two frames, so the
 *   drawer is laid out and painted before it starts to move.
 * - A close ends only when the dim layer behind the menu has finished fading as well. Before, the
 *   menu was marked closed after 150 ms and cut that 340 ms fade off half-way.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  /* Only swallows the same tap delivered twice (touch + click); a real second tap always works. */
  static DUPLICATE_TAP_MS = 180;
  static RTL_HIDDEN_CLIP = 'inset(0px 0px 0px 100%)';
  static RTL_SHOWN_CLIP = 'inset(0px 0px 0px 0%)';
  /* One timeline for the whole menu. The dim layer and the page blur use the same two durations
     in assets/ps-header-menu-filter-fix.css: change them together. */
  static OPEN_MS = 250;
  static CLOSE_MS = 200;
  /* Two frames for the browser to lay out and paint the drawer before it starts to move. */
  static PREPARE_MS = 34;
  /* The CSS fades start a frame after the class change; the close waits for them to end. */
  static SETTLE_MS = 40;

  #lastToggleAt = -Infinity;
  #closeToken = 0;
  #scrollLocked = false;
  #touchAllowed = false;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#unlockScroll();
  }

  #isRtl() {
    return document.documentElement.dir === 'rtl';
  }

  /**
   * The fully closed position, in pixels (the same place as the CSS translateX(-100%)): a pixel
   * transform can always run on the compositor.
   * @param {HTMLElement} drawer
   * @returns {string}
   */
  #closedTransform(drawer) {
    const width = drawer.offsetWidth;
    return `translateX(${this.#isRtl() ? width : -width}px)`;
  }

  /** @param {number} ms */
  #duration(ms) {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 1 : ms;
  }

  /** @param {HTMLElement} drawer */
  #drawerChildren(drawer) {
    return /** @type {HTMLElement[]} */ ([...drawer.children].filter((child) => child instanceof HTMLElement));
  }

  /** @param {HTMLElement} drawer */
  #cancelDrawerAnimations(drawer) {
    drawer.getAnimations().forEach((animation) => animation.cancel());
    this.#drawerChildren(drawer).forEach((child) => child.getAnimations().forEach((animation) => animation.cancel()));
  }

  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;
    this.#close(this.#getDetailsElement(event));
  };

  /**
   * The scrollable area inside the menu under the finger, if any. Checks the real overflow of
   * every element up to the menu (before, only a few fixed class names were checked, so the menu
   * list, which scrolls in its own container, could not be scrolled at all).
   * @param {EventTarget | null} target
   * @returns {HTMLElement | null}
   */
  #scrollerFor(target) {
    const drawer = this.refs.details.querySelector('[data-header-drawer]');
    if (!(drawer instanceof HTMLElement) || !(target instanceof Element) || !drawer.contains(target)) return null;

    for (let node = target; node && node !== drawer.parentElement; node = node.parentElement) {
      if (!(node instanceof HTMLElement)) continue;
      const overflowY = getComputedStyle(node).overflowY;
      if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight + 1) return node;
    }
    return null;
  }

  /* Decide once per gesture (not on every touchmove) whether it may scroll. */
  #touchStart = (event) => {
    const scroller = this.#scrollerFor(event.target);
    this.#touchAllowed = Boolean(scroller);
    /* Reaching the end of the menu list must not start scrolling the page behind it. */
    if (scroller) scroller.style.setProperty('overscroll-behavior-y', 'contain');
  };

  /* Block page scrolling while the menu is open; scrolling inside the menu is allowed. */
  #touchGuard = (event) => {
    if (this.#touchAllowed) return;
    if (event.cancelable) event.preventDefault();
  };

  #lockScroll() {
    if (this.#scrollLocked) return;
    this.#scrollLocked = true;
    this.#touchAllowed = false;
    document.addEventListener('touchstart', this.#touchStart, { passive: true, capture: true });
    document.addEventListener('touchmove', this.#touchGuard, { passive: false });
  }

  #unlockScroll() {
    if (!this.#scrollLocked) return;
    this.#scrollLocked = false;
    this.#touchAllowed = false;
    document.removeEventListener('touchstart', this.#touchStart, { capture: true });
    document.removeEventListener('touchmove', this.#touchGuard);
  }

  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;
    return event.target.closest('details') ?? this.refs.details;
  }

  toggle(event) {
    event?.preventDefault();
    event?.stopPropagation();

    const now = performance.now();
    if (now - this.#lastToggleAt < HeaderDrawer.DUPLICATE_TAP_MS) return;
    this.#lastToggleAt = now;

    const details = this.refs.details;
    const isClosing = details.classList.contains('menu-closing');

    if (details.hasAttribute('open') && !isClosing) {
      this.close();
      return;
    }

    this.open();
  }

  open() {
    const details = this.refs.details;
    const summary = details.querySelector(':scope > summary');
    if (!summary) return;

    const drawer = details.querySelector('[data-header-drawer]');
    const rtl = this.#isRtl();
    const wasClosing = details.classList.contains('menu-closing');

    /* Starting points, taken from where the drawer is right now if a close is still animating
       (null: from the fully closed position). */
    let fromTransform = null;
    let fromClip = HeaderDrawer.RTL_HIDDEN_CLIP;
    let fromChild = null;

    if (drawer instanceof HTMLElement && wasClosing) {
      const drawerStyle = getComputedStyle(drawer);
      if (rtl) {
        if (drawerStyle.clipPath && drawerStyle.clipPath !== 'none') fromClip = drawerStyle.clipPath;
        const firstChild = this.#drawerChildren(drawer)[0];
        const childTransform = firstChild ? getComputedStyle(firstChild).transform : 'none';
        fromChild = childTransform && childTransform !== 'none' ? childTransform : 'translateX(0px)';
      } else if (drawerStyle.transform && drawerStyle.transform !== 'none') {
        fromTransform = drawerStyle.transform;
      }
    }

    /* Invalidate any pending close so its finish step cannot reset the open menu. */
    this.#closeToken += 1;

    if (!details.hasAttribute('open')) details.setAttribute('open', '');
    details.classList.remove('menu-closing');
    summary.setAttribute('aria-expanded', 'true');
    this.#lockScroll();

    const start = () => {
      if (!details.hasAttribute('open') || details.classList.contains('menu-closing')) return;

      details.classList.add('menu-open');

      if (drawer instanceof HTMLElement) {
        this.#cancelDrawerAnimations(drawer);
        /* .menu-open keeps the drawer visible from here on; drop what an interrupted close left. */
        drawer.style.removeProperty('visibility');

        const closed = this.#closedTransform(drawer);
        const timing = {
          duration: this.#duration(HeaderDrawer.OPEN_MS),
          /* During the pause the drawer already sits at its starting point (fill: both). */
          delay: wasClosing ? 0 : HeaderDrawer.PREPARE_MS,
          easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
          fill: 'both',
        };

        if (rtl) {
          const reveal = drawer.animate(
            [{ clipPath: fromClip }, { clipPath: HeaderDrawer.RTL_SHOWN_CLIP }],
            timing
          );
          const slides = this.#drawerChildren(drawer).map((child) =>
            child.animate([{ transform: fromChild ?? closed }, { transform: 'translateX(0px)' }], timing)
          );
          reveal.finished
            .then(() => {
              reveal.cancel();
              slides.forEach((animation) => animation.cancel());
            })
            .catch(() => {});
        } else {
          const openingAnimation = drawer.animate(
            [{ transform: fromTransform ?? closed }, { transform: 'translateX(0px)' }],
            timing
          );
          openingAnimation.finished.then(() => openingAnimation.cancel()).catch(() => {});
        }
      }

      setTimeout(() => trapFocus(details), 0);
    };

    if (wasClosing) {
      /* Reopened in the middle of a close: the drawer is on screen, so turn it around in the same
         frame (cancelling the close and waiting a frame made it blink). */
      start();
    } else {
      if (drawer instanceof HTMLElement) this.#cancelDrawerAnimations(drawer);
      /* A frame later, so the freshly opened <details> has rendered its closed state first. */
      requestAnimationFrame(start);
    }
  }

  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  close() {
    this.#close(this.refs.details);
  }

  #close(details) {
    const summary = details.querySelector('summary');
    if (!summary || details.classList.contains('menu-closing')) return;

    const isMainDrawer = details === this.refs.details;
    summary.setAttribute('aria-expanded', 'false');
    if (isMainDrawer) this.#unlockScroll();

    const token = isMainDrawer ? ++this.#closeToken : 0;

    const finishClose = () => {
      /* The menu was reopened while this close was animating: leave it open. */
      if (isMainDrawer && token !== this.#closeToken) return;

      details.classList.remove('menu-closing');
      reset(details);

      if (isMainDrawer) {
        const closedDrawer = details.querySelector('[data-header-drawer]');
        if (closedDrawer instanceof HTMLElement) closedDrawer.style.removeProperty('visibility');
        removeTrapFocus();
        this.querySelectorAll('details[open]').forEach(reset);
      } else {
        setTimeout(() => trapFocus(this.refs.details), 0);
      }
    };

    if (isMainDrawer) {
      const drawer = details.querySelector('[data-header-drawer]');
      if (drawer instanceof HTMLElement) {
        const rtl = this.#isRtl();
        const timing = {
          duration: this.#duration(HeaderDrawer.CLOSE_MS),
          easing: 'cubic-bezier(0.4, 0, 1, 1)',
          fill: 'both',
        };
        const closed = this.#closedTransform(drawer);

        /* Close from the current position, even mid-opening. */
        const drawerStyle = getComputedStyle(drawer);
        const currentTransform = drawerStyle.transform;
        const currentClip = drawerStyle.clipPath;
        const firstChild = this.#drawerChildren(drawer)[0];
        const currentChild = firstChild ? getComputedStyle(firstChild).transform : 'none';

        this.#cancelDrawerAnimations(drawer);

        /* Visible until the close is over, on every screen size (the keyframes no longer carry
           visibility); finishClose and open() remove it again. */
        drawer.style.setProperty('visibility', 'visible');
        details.classList.add('menu-closing');
        details.classList.remove('menu-open');

        let closingAnimation;
        let childAnimations = [];

        if (rtl) {
          closingAnimation = drawer.animate(
            [
              { clipPath: currentClip && currentClip !== 'none' ? currentClip : HeaderDrawer.RTL_SHOWN_CLIP },
              { clipPath: HeaderDrawer.RTL_HIDDEN_CLIP },
            ],
            timing
          );
          childAnimations = this.#drawerChildren(drawer).map((child) =>
            child.animate(
              [
                { transform: currentChild && currentChild !== 'none' ? currentChild : 'translateX(0px)' },
                { transform: closed },
              ],
              timing
            )
          );
        } else {
          closingAnimation = drawer.animate(
            [
              { transform: currentTransform === 'none' ? 'translateX(0px)' : currentTransform },
              { transform: closed },
            ],
            timing
          );
        }

        /* The dim layer and the page blur fade out in CSS over the same time. */
        const fades = new Promise((resolve) => setTimeout(resolve, timing.duration + HeaderDrawer.SETTLE_MS));

        Promise.all([closingAnimation.finished, fades]).then(
          () => {
            if (token !== this.#closeToken) return;
            closingAnimation.cancel();
            childAnimations.forEach((animation) => animation.cancel());
            finishClose();
          },
          finishClose
        );
        return;
      }

      details.classList.remove('menu-open');
      finishClose();
      return;
    }

    details.classList.remove('menu-open');
    onAnimationEnd(details, finishClose);
  }

  #setupAnimatedElementListeners() {
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }

    this.querySelectorAll('.menu-drawer__animated-element').forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

function reset(element) {
  element.classList.remove('menu-open', 'menu-closing');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
